import type {
  AccountDto,
  AddMemberRequest,
  ChangePasswordRequest,
  MemberDto,
  RolesResponse,
  UpdateMemberRequest,
  UpsertCustomRoleRequest,
  CompanySettingsResponse,
  CompanyBrandingResponse,
  UpdateCompanyBrandingRequest,
  DashboardResponse,
  DashboardPeriodPreset,
  EtaConnectionDto,
  EtaConnectionStatusDto,
  EtaConnectionTestResult,
  EtaDocumentPreview,
  EtaReadinessResponse,
  UpdateEtaConnectionRequest,
  UpdateCompanySettingsRequest,
  BankAccountDto,
  CashboxDto,
  CreateBankAccountRequest,
  CreateCashboxRequest,
  CreateReconciliationRequest,
  ImportStatementRequest,
  ImportStatementResponse,
  StatementMatchesResponse,
  ReconciliationDto,
  ReconciliationWorkspace,
  TreasuryAccountsResponse,
  AccountLedgerResponse,
  ApAgingResponse,
  CashExpenseDto,
  CreateCashExpenseRequest,
  CreatePurchaseBillRequest,
  CancelPurchaseBillRequest,
  CreatePurchaseNoteRequest,
  PurchaseNoteDto,
  PurchaseNoteSummary,
  WithholdingPayableReportResponse,
  CreateSupplierPaymentRequest,
  CreateSupplierRequest,
  CreateTreasuryTransferRequest,
  PurchaseBillDto,
  PurchaseBillSummary,
  SupplierDto,
  SupplierPaymentDto,
  SupplierStatementResponse,
  TreasuryTransferDto,
  ArAgingResponse,
  BalanceSheetResponse,
  CompanyDto,
  CreateCustomerRequest,
  CreateSalesInvoiceRequest,
  CancelSalesInvoiceRequest,
  CatalogItemDto,
  CreateCatalogItemRequest,
  CreateSalesNoteRequest,
  SalesNoteDto,
  SalesNoteSummary,
  UpdateCatalogItemRequest,
  CreateCustomerReceiptRequest,
  CreateReceiptApplicationRequest,
  ReceiptApplicationDto,
  WithholdingReportResponse,
  VatReturnResponse,
  SalesAnalysisResponse,
  PurchasesAnalysisResponse,
  TreasuryMovementResponse,
  DebtsReportResponse,
  TabularReportResponse,
  CustomerReceiptDto,
  CustomerStatementResponse,
  CustomerDto,
  FiscalPeriodDto,
  PeriodCloseChecksResponse,
  YearEndCloseResponse,
  GeneralLedgerResponse,
  IncomeStatementResponse,
  JournalDto,
  LoginRequest,
  MfaConfirmResponse,
  SalesInvoiceDto,
  SalesInvoiceSummary,
  MfaEnrollResponse,
  OpeningBalanceRequest,
  PostJournalRequest,
  ProblemDetails,
  SessionResponse,
  SessionUserDto,
  TrialBalanceResponse,
  CreateStockAdjustmentRequest,
  CreateStockOpeningRequest,
  StockCardResponse,
  StockDocumentDto,
  StockOnHandResponse,
  StockValuationResponse,
  CreatePlatformCompanyRequest,
  PlatformCompanyDetail,
  PlatformCompanyDto,
  PlatformConfigDto,
  ReactivateCompanyRequest,
  SuspendCompanyRequest,
  UpdatePlatformCompanyRequest,
  CreateMigrationBatchRequest,
  MigrationBatchDto,
  MigrationBatchSummary,
  MigrationFileDto,
  MigrationKind,
  MigrationProfileDto,
  MigrationReconciliationResponse,
  MigrationReportResponse,
  MigrationRowDto,
  MigrationRowsResponse,
  UpdateMappingRequest,
  UpdateMigrationRowsRequest,
} from '@hesabat/contracts';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000/v1';

export interface HealthResponse {
  status: string;
  service: string;
  database: 'up' | 'down';
  timestamp: string;
}

/** 401 من الـ API يعنى «سجّل الدخول»، لا «عطل» — الواجهة تفرّق بينهما. */
export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

/**
 * 403 بنوع MFA يعنى «الجلسة قائمة وناقصها العامل الثانى» — لا تُمسح الجلسة
 * ولا يُعاد إلى شاشة كلمة السر، بل إلى الخطوة الناقصة التى يحملها `status`.
 */
export class MfaPendingError extends Error {
  constructor(readonly status: 'mfa_required' | 'mfa_enrollment_required') {
    super(status);
    this.name = 'MfaPendingError';
  }
}

/** تجاوز حد المحاولات — رسالة تقول متى يُعاد لا «عطل فى الخدمة». */
export class RateLimitedError extends Error {
  constructor(readonly retryAfterSeconds: number) {
    super('rate limited');
    this.name = 'RateLimitedError';
  }
}

/** رمز تحقق خاطئ — رسالة واحدة من الخادم لكل الأسباب. */
export class InvalidCodeError extends Error {
  constructor(readonly detail?: string) {
    super('invalid code');
    this.name = 'InvalidCodeError';
  }
}

/**
 * رفض مفهوم من الـ API (400 أو 403 أو 404 أو 409 أو 422) بجسم `problem+json`.
 * يحمل `issues` كما هى: النموذج يضع كل مشكلة تحت سطرها بـ `lineIndex`.
 */
export class ProblemError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ProblemError';
  }
}

/** ردّ الترحيل: القيد ومعه هل كان إعادة لطلب سابق بنفس المفتاح. */
export type PostedJournal = JournalDto & { replayed: boolean };

/**
 * كل طلب يحمل كوكى الجلسة httpOnly — لا رمز ولا سر فى كود الواجهة ولا فى
 * localStorage، فلا شىء تسرقه ثغرة XSS (ADR-0004). `credentials: 'include'`
 * إلزامى لأن الواجهة والـ API على منفذين مختلفين.
 */
function queryString(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  return search.toString();
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(BASE_URL + path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 429) {
    throw new RateLimitedError(Number(response.headers.get('retry-after') ?? 60));
  }

  if (response.status === 401) {
    // مسار التحقق يردّ 401 على رمز خاطئ، والشاشة تبقى مكانها لا تعود للدخول
    if (path.startsWith('/auth/mfa/')) {
      throw new InvalidCodeError(await detailOf(response));
    }
    throw new UnauthorizedError();
  }

  if (response.status === 403) {
    const type = await typeOf(response);
    if (type === 'urn:hesabat:mfa-required') {
      throw new MfaPendingError('mfa_required');
    }
    if (type === 'urn:hesabat:mfa-enrollment-required') {
      throw new MfaPendingError('mfa_enrollment_required');
    }
  }

  if (!response.ok) {
    const problem = await problemOf(response);
    if (problem && typeof problem.title === 'string' && typeof problem.status === 'number') {
      throw new ProblemError(problem as ProblemDetails);
    }
    const body = await response.text();
    throw new Error('HTTP ' + response.status + ': ' + body.slice(0, 300));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  health: () => request<HealthResponse>('/health'),

  login: (credentials: LoginRequest) =>
    request<SessionResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  mfaEnroll: () => request<MfaEnrollResponse>('/auth/mfa/enroll', { method: 'POST' }),

  mfaConfirm: (code: string) =>
    request<MfaConfirmResponse>('/auth/mfa/enroll/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  mfaVerify: (code: string) =>
    request<SessionResponse>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  me: () => request<SessionUserDto>('/auth/me'),

  companies: () => request<CompanyDto[]>('/companies'),

  accounts: (companyId: string) =>
    request<AccountDto[]>('/companies/' + companyId + '/accounts?postableOnly=true'),

  dashboard: (
    companyId: string,
    asOf: string,
    period: DashboardPeriodPreset = 'month',
    from?: string,
  ) =>
    request<DashboardResponse>(
      '/companies/' +
        companyId +
        '/dashboard?' +
        new URLSearchParams({ asOf, period, ...(period === 'custom' && from ? { from } : {}) }),
    ),

  trialBalance: (companyId: string, from: string, to: string) =>
    request<TrialBalanceResponse>(
      '/companies/' + companyId + '/reports/trial-balance?from=' + from + '&to=' + to,
    ),

  accountLedger: (companyId: string, accountCode: string, from: string, to: string) =>
    request<AccountLedgerResponse>(
      '/companies/' +
        companyId +
        '/accounts/' +
        encodeURIComponent(accountCode) +
        '/ledger?from=' +
        from +
        '&to=' +
        to,
    ),

  generalLedger: (companyId: string, from: string, to: string) =>
    request<GeneralLedgerResponse>(
      '/companies/' + companyId + '/reports/general-ledger?from=' + from + '&to=' + to,
    ),

  incomeStatement: (companyId: string, from: string, to: string) =>
    request<IncomeStatementResponse>(
      '/companies/' + companyId + '/reports/income-statement?from=' + from + '&to=' + to,
    ),

  balanceSheet: (companyId: string, asOf: string) =>
    request<BalanceSheetResponse>(
      '/companies/' + companyId + '/reports/balance-sheet?asOf=' + asOf,
    ),

  customers: (companyId: string) =>
    request<CustomerDto[]>('/companies/' + companyId + '/customers'),

  createCustomer: (companyId: string, idempotencyKey: string, body: CreateCustomerRequest) =>
    request<CustomerDto & { replayed: boolean }>('/companies/' + companyId + '/customers', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  salesInvoices: (companyId: string, customerId?: string, openOnly = false) => {
    const query = new URLSearchParams();
    if (customerId) query.set('customerId', customerId);
    if (openOnly) query.set('openOnly', 'true');
    const suffix = query.toString();
    return request<SalesInvoiceSummary[]>(
      '/companies/' + companyId + '/sales-invoices' + (suffix ? '?' + suffix : ''),
    );
  },

  customerReceipts: (companyId: string) =>
    request<CustomerReceiptDto[]>('/companies/' + companyId + '/customer-receipts'),

  createCustomerReceipt: (
    companyId: string,
    idempotencyKey: string,
    body: CreateCustomerReceiptRequest,
  ) =>
    request<CustomerReceiptDto & { replayed: boolean }>(
      '/companies/' + companyId + '/customer-receipts',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  receiptApplications: (companyId: string) =>
    request<ReceiptApplicationDto[]>('/companies/' + companyId + '/receipt-applications'),

  createReceiptApplication: (
    companyId: string,
    idempotencyKey: string,
    body: CreateReceiptApplicationRequest,
  ) =>
    request<ReceiptApplicationDto & { replayed: boolean }>(
      '/companies/' + companyId + '/receipt-applications',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  /** تقرير جدولى من الفهرس (السجلات وأكبر العملاء والموردين). */
  catalogReport: (
    companyId: string,
    reportId: string,
    params: Record<string, string | undefined>,
  ) =>
    request<TabularReportResponse>(
      '/companies/' + companyId + '/reports/catalog/' + reportId + '?' + queryString(params),
    ),

  auditLog: (companyId: string, params: Record<string, string | undefined>) =>
    request<TabularReportResponse>(
      '/companies/' + companyId + '/reports/audit-log?' + queryString(params),
    ),

  /** مركز التقارير — المعاملات كما فى العقد، والفارغ منها لا يُرسل. */
  salesAnalysis: (companyId: string, params: Record<string, string | undefined>) =>
    request<SalesAnalysisResponse>(
      '/companies/' + companyId + '/reports/sales-analysis?' + queryString(params),
    ),

  purchasesAnalysis: (companyId: string, params: Record<string, string | undefined>) =>
    request<PurchasesAnalysisResponse>(
      '/companies/' + companyId + '/reports/purchases-analysis?' + queryString(params),
    ),

  treasuryMovement: (companyId: string, params: Record<string, string | undefined>) =>
    request<TreasuryMovementResponse>(
      '/companies/' + companyId + '/reports/treasury-movement?' + queryString(params),
    ),

  debtsReport: (companyId: string, params: Record<string, string | undefined>) =>
    request<DebtsReportResponse>(
      '/companies/' + companyId + '/reports/debts?' + queryString(params),
    ),

  vatReturn: (companyId: string, from: string, to: string) =>
    request<VatReturnResponse>(
      '/companies/' + companyId + '/reports/vat-return?from=' + from + '&to=' + to,
    ),

  withholdingReport: (companyId: string, from: string, to: string) =>
    request<WithholdingReportResponse>(
      '/companies/' + companyId + '/reports/withholding-receivable?from=' + from + '&to=' + to,
    ),

  customerStatement: (companyId: string, customerId: string, from: string, to: string) =>
    request<CustomerStatementResponse>(
      '/companies/' +
        companyId +
        '/customers/' +
        customerId +
        '/statement?from=' +
        from +
        '&to=' +
        to,
    ),

  arAging: (companyId: string, asOf: string) =>
    request<ArAgingResponse>('/companies/' + companyId + '/reports/ar-aging?asOf=' + asOf),

  salesInvoice: (companyId: string, invoiceId: string) =>
    request<SalesInvoiceDto>('/companies/' + companyId + '/sales-invoices/' + invoiceId),

  createSalesInvoice: (
    companyId: string,
    idempotencyKey: string,
    body: CreateSalesInvoiceRequest,
  ) =>
    request<SalesInvoiceDto & { replayed: boolean }>(
      '/companies/' + companyId + '/sales-invoices',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  cancelSalesInvoice: (
    companyId: string,
    invoiceId: string,
    idempotencyKey: string,
    body: CancelSalesInvoiceRequest,
  ) =>
    request<SalesInvoiceDto & { replayed: boolean }>(
      '/companies/' + companyId + '/sales-invoices/' + invoiceId + '/cancel',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  salesNote: (companyId: string, noteId: string) =>
    request<SalesNoteDto>('/companies/' + companyId + '/sales-notes/' + noteId),

  salesNotes: (companyId: string, invoiceId: string) =>
    request<SalesNoteSummary[]>('/companies/' + companyId + '/sales-notes?invoiceId=' + invoiceId),

  createSalesNote: (companyId: string, idempotencyKey: string, body: CreateSalesNoteRequest) =>
    request<SalesNoteDto & { replayed: boolean }>('/companies/' + companyId + '/sales-notes', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  catalogItems: (companyId: string, activeOnly = false) =>
    request<CatalogItemDto[]>(
      '/companies/' + companyId + '/catalog-items' + (activeOnly ? '?activeOnly=true' : ''),
    ),

  createCatalogItem: (companyId: string, idempotencyKey: string, body: CreateCatalogItemRequest) =>
    request<CatalogItemDto & { replayed: boolean }>('/companies/' + companyId + '/catalog-items', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  updateCatalogItem: (companyId: string, itemId: string, body: UpdateCatalogItemRequest) =>
    request<CatalogItemDto>('/companies/' + companyId + '/catalog-items/' + itemId, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  suppliers: (companyId: string) =>
    request<SupplierDto[]>('/companies/' + companyId + '/suppliers'),

  createSupplier: (companyId: string, idempotencyKey: string, body: CreateSupplierRequest) =>
    request<SupplierDto & { replayed: boolean }>('/companies/' + companyId + '/suppliers', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  supplierStatement: (companyId: string, supplierId: string, from: string, to: string) =>
    request<SupplierStatementResponse>(
      '/companies/' +
        companyId +
        '/suppliers/' +
        supplierId +
        '/statement?from=' +
        from +
        '&to=' +
        to,
    ),

  purchaseBills: (companyId: string, supplierId?: string, openOnly = false) => {
    const query = new URLSearchParams();
    if (supplierId) query.set('supplierId', supplierId);
    if (openOnly) query.set('openOnly', 'true');
    const suffix = query.toString();
    return request<PurchaseBillSummary[]>(
      '/companies/' + companyId + '/purchase-bills' + (suffix ? '?' + suffix : ''),
    );
  },

  purchaseBill: (companyId: string, billId: string) =>
    request<PurchaseBillDto>('/companies/' + companyId + '/purchase-bills/' + billId),

  createPurchaseBill: (
    companyId: string,
    idempotencyKey: string,
    body: CreatePurchaseBillRequest,
  ) =>
    request<PurchaseBillDto & { replayed: boolean }>(
      '/companies/' + companyId + '/purchase-bills',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  cancelPurchaseBill: (
    companyId: string,
    billId: string,
    idempotencyKey: string,
    body: CancelPurchaseBillRequest,
  ) =>
    request<PurchaseBillDto & { replayed: boolean }>(
      '/companies/' + companyId + '/purchase-bills/' + billId + '/cancel',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  purchaseNotes: (companyId: string, billId: string) =>
    request<PurchaseNoteSummary[]>('/companies/' + companyId + '/purchase-notes?billId=' + billId),

  createPurchaseNote: (
    companyId: string,
    idempotencyKey: string,
    body: CreatePurchaseNoteRequest,
  ) =>
    request<PurchaseNoteDto & { replayed: boolean }>(
      '/companies/' + companyId + '/purchase-notes',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  withholdingPayableReport: (companyId: string, from: string, to: string) =>
    request<WithholdingPayableReportResponse>(
      '/companies/' + companyId + '/reports/withholding-payable?from=' + from + '&to=' + to,
    ),

  supplierPayments: (companyId: string) =>
    request<SupplierPaymentDto[]>('/companies/' + companyId + '/supplier-payments'),

  createSupplierPayment: (
    companyId: string,
    idempotencyKey: string,
    body: CreateSupplierPaymentRequest,
  ) =>
    request<SupplierPaymentDto & { replayed: boolean }>(
      '/companies/' + companyId + '/supplier-payments',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  apAging: (companyId: string, asOf: string) =>
    request<ApAgingResponse>('/companies/' + companyId + '/reports/ap-aging?asOf=' + asOf),

  cashExpenses: (companyId: string) =>
    request<CashExpenseDto[]>('/companies/' + companyId + '/cash-expenses'),

  createCashExpense: (companyId: string, idempotencyKey: string, body: CreateCashExpenseRequest) =>
    request<CashExpenseDto & { replayed: boolean }>('/companies/' + companyId + '/cash-expenses', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  members: (companyId: string) => request<MemberDto[]>('/companies/' + companyId + '/members'),

  addMember: (companyId: string, body: AddMemberRequest) =>
    request<MemberDto[]>('/companies/' + companyId + '/members', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateMember: (companyId: string, userId: string, body: UpdateMemberRequest) =>
    request<MemberDto[]>('/companies/' + companyId + '/members/' + userId, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  removeMember: (companyId: string, userId: string) =>
    request<MemberDto[]>('/companies/' + companyId + '/members/' + userId, { method: 'DELETE' }),

  roles: (companyId: string) => request<RolesResponse>('/companies/' + companyId + '/roles'),

  createRole: (companyId: string, body: UpsertCustomRoleRequest) =>
    request<RolesResponse>('/companies/' + companyId + '/roles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateRole: (companyId: string, roleId: string, body: UpsertCustomRoleRequest) =>
    request<RolesResponse>('/companies/' + companyId + '/roles/' + roleId, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteRole: (companyId: string, roleId: string) =>
    request<RolesResponse>('/companies/' + companyId + '/roles/' + roleId, { method: 'DELETE' }),

  changePassword: (body: ChangePasswordRequest) =>
    request<{ status: string; expiresAt: string }>('/auth/password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  stockOnHand: (companyId: string, asOf: string) =>
    request<StockOnHandResponse>('/companies/' + companyId + '/inventory/stock?asOf=' + asOf),

  stockValuation: (companyId: string, asOf: string) =>
    request<StockValuationResponse>(
      '/companies/' + companyId + '/inventory/valuation?asOf=' + asOf,
    ),

  stockCard: (companyId: string, itemId: string, from: string, to: string) =>
    request<StockCardResponse>(
      '/companies/' + companyId + '/inventory/items/' + itemId + '/card?from=' + from + '&to=' + to,
    ),

  stockDocuments: (companyId: string) =>
    request<StockDocumentDto[]>('/companies/' + companyId + '/inventory/documents'),

  createStockOpening: (
    companyId: string,
    idempotencyKey: string,
    body: CreateStockOpeningRequest,
  ) =>
    request<StockDocumentDto & { replayed: boolean }>(
      '/companies/' + companyId + '/inventory/opening',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  createStockAdjustment: (
    companyId: string,
    idempotencyKey: string,
    body: CreateStockAdjustmentRequest,
  ) =>
    request<StockDocumentDto & { replayed: boolean }>(
      '/companies/' + companyId + '/inventory/adjustments',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  companySettings: (companyId: string) =>
    request<CompanySettingsResponse>('/companies/' + companyId + '/settings'),

  updateCompanySettings: (companyId: string, body: UpdateCompanySettingsRequest) =>
    request<CompanySettingsResponse>('/companies/' + companyId + '/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  companyBranding: (companyId: string) =>
    request<CompanyBrandingResponse>('/companies/' + companyId + '/branding'),

  updateCompanyBranding: (companyId: string, body: UpdateCompanyBrandingRequest) =>
    request<CompanyBrandingResponse>('/companies/' + companyId + '/branding', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** البايتات خام لا JSON — الخادم يستنتج النوع من المحتوى، والترويسة للمحلل فقط. */
  uploadCompanyLogo: (companyId: string, file: Blob) =>
    request<CompanyBrandingResponse>('/companies/' + companyId + '/branding/logo', {
      method: 'PUT',
      headers: { 'content-type': file.type || 'application/octet-stream' },
      body: file,
    }),

  deleteCompanyLogo: (companyId: string) =>
    request<CompanyBrandingResponse>('/companies/' + companyId + '/branding/logo', {
      method: 'DELETE',
    }),

  /**
   * الصورة تُجلب بـ fetch وكوكى الجلسة ثم تُعرض من blob محلى: `<img src>` مباشر على
   * منفذ الـ API يصطدم بسياسة الموارد عبر الأصول، والمسار يحتاج الجلسة أصلاً.
   */
  companyLogo: async (companyId: string): Promise<Blob | null> => {
    const response = await fetch(BASE_URL + '/companies/' + companyId + '/branding/logo', {
      credentials: 'include',
    });
    if (response.status === 404) return null;
    if (response.status === 401) throw new UnauthorizedError();
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return response.blob();
  },

  eInvoiceStatus: (companyId: string) =>
    request<EtaConnectionStatusDto>('/companies/' + companyId + '/e-invoice/status'),

  eInvoiceConnection: (companyId: string) =>
    request<EtaConnectionDto>('/companies/' + companyId + '/e-invoice/connection'),

  updateEInvoiceConnection: (companyId: string, body: UpdateEtaConnectionRequest) =>
    request<EtaConnectionDto>('/companies/' + companyId + '/e-invoice/connection', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  testEInvoiceConnection: (companyId: string) =>
    request<EtaConnectionTestResult>('/companies/' + companyId + '/e-invoice/connection/test', {
      method: 'POST',
    }),

  eInvoiceReadiness: (companyId: string) =>
    request<EtaReadinessResponse>('/companies/' + companyId + '/e-invoice/readiness'),

  eInvoiceDocument: (companyId: string, invoiceId: string) =>
    request<EtaDocumentPreview>(
      '/companies/' + companyId + '/e-invoice/invoices/' + invoiceId + '/document',
    ),

  treasuryAccounts: (companyId: string) =>
    request<TreasuryAccountsResponse>('/companies/' + companyId + '/treasury-accounts'),

  createCashbox: (companyId: string, idempotencyKey: string, body: CreateCashboxRequest) =>
    request<CashboxDto & { replayed: boolean }>('/companies/' + companyId + '/cashboxes', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  createBankAccount: (companyId: string, idempotencyKey: string, body: CreateBankAccountRequest) =>
    request<BankAccountDto & { replayed: boolean }>('/companies/' + companyId + '/bank-accounts', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  setTreasuryActive: (
    companyId: string,
    kind: 'cashboxes' | 'bank-accounts',
    id: string,
    isActive: boolean,
  ) =>
    request<TreasuryAccountsResponse>(
      '/companies/' + companyId + '/' + kind + '/' + id + '/active',
      { method: 'POST', body: JSON.stringify({ isActive }) },
    ),

  reconciliationWorkspace: (companyId: string, bankAccountId: string, statementDate: string) =>
    request<ReconciliationWorkspace>(
      '/companies/' +
        companyId +
        '/bank-accounts/' +
        bankAccountId +
        '/reconciliation?statementDate=' +
        statementDate,
    ),

  reconciliations: (companyId: string, bankAccountId: string) =>
    request<ReconciliationDto[]>(
      '/companies/' + companyId + '/bank-accounts/' + bankAccountId + '/reconciliations',
    ),

  createReconciliation: (
    companyId: string,
    bankAccountId: string,
    idempotencyKey: string,
    body: CreateReconciliationRequest,
  ) =>
    request<ReconciliationDto & { replayed: boolean }>(
      '/companies/' + companyId + '/bank-accounts/' + bankAccountId + '/reconciliations',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  importStatement: (
    companyId: string,
    bankAccountId: string,
    idempotencyKey: string,
    body: ImportStatementRequest,
  ) =>
    request<ImportStatementResponse & { replayed: boolean }>(
      '/companies/' + companyId + '/bank-accounts/' + bankAccountId + '/statement-imports',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  deleteStatementImport: (companyId: string, bankAccountId: string, importId: string) =>
    request<{ importId: string; deleted: number }>(
      '/companies/' +
        companyId +
        '/bank-accounts/' +
        bankAccountId +
        '/statement-imports/' +
        importId,
      { method: 'DELETE' },
    ),

  statementMatches: (companyId: string, bankAccountId: string, statementDate: string) =>
    request<StatementMatchesResponse>(
      '/companies/' +
        companyId +
        '/bank-accounts/' +
        bankAccountId +
        '/statement-matches?statementDate=' +
        statementDate,
    ),

  treasuryTransfers: (companyId: string) =>
    request<TreasuryTransferDto[]>('/companies/' + companyId + '/treasury-transfers'),

  createTreasuryTransfer: (
    companyId: string,
    idempotencyKey: string,
    body: CreateTreasuryTransferRequest,
  ) =>
    request<TreasuryTransferDto & { replayed: boolean }>(
      '/companies/' + companyId + '/treasury-transfers',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify(body),
      },
    ),

  periods: (companyId: string) =>
    request<FiscalPeriodDto[]>('/companies/' + companyId + '/fiscal-periods'),

  closePeriod: (
    companyId: string,
    periodId: string,
    idempotencyKey: string,
    acknowledgeWarnings = false,
  ) =>
    request<FiscalPeriodDto>('/companies/' + companyId + '/fiscal-periods/' + periodId + '/close', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify({ acknowledgeWarnings }),
    }),

  periodCloseChecks: (companyId: string, periodId: string) =>
    request<PeriodCloseChecksResponse>(
      '/companies/' + companyId + '/fiscal-periods/' + periodId + '/close-checks',
    ),

  yearEndClose: (companyId: string, periodId: string, idempotencyKey: string) =>
    request<YearEndCloseResponse>(
      '/companies/' + companyId + '/fiscal-periods/' + periodId + '/year-end-close',
      { method: 'POST', headers: { 'idempotency-key': idempotencyKey } },
    ),

  reopenPeriod: (companyId: string, periodId: string, idempotencyKey: string, reason: string) =>
    request<FiscalPeriodDto>(
      '/companies/' + companyId + '/fiscal-periods/' + periodId + '/reopen',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify({ reason }),
      },
    ),

  postOpeningBalance: (companyId: string, idempotencyKey: string, body: OpeningBalanceRequest) =>
    request<PostedJournal>('/companies/' + companyId + '/opening-balances', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  /** المفتاح من النموذج لا من هنا: إعادة الإرسال بعد انقطاع تحمل نفس المفتاح. */
  postJournal: (companyId: string, idempotencyKey: string, body: PostJournalRequest) =>
    request<PostedJournal>('/companies/' + companyId + '/journals', {
      method: 'POST',
      headers: { 'idempotency-key': idempotencyKey },
      body: JSON.stringify(body),
    }),

  platformConfig: () => request<PlatformConfigDto>('/platform/config'),

  platformCompanies: () => request<PlatformCompanyDto[]>('/platform/companies'),

  platformCompany: (id: string) => request<PlatformCompanyDetail>('/platform/companies/' + id),

  createPlatformCompany: (body: CreatePlatformCompanyRequest) =>
    request<PlatformCompanyDetail>('/platform/companies', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updatePlatformCompany: (id: string, body: UpdatePlatformCompanyRequest) =>
    request<PlatformCompanyDetail>('/platform/companies/' + id, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  suspendCompany: (id: string, body: SuspendCompanyRequest) =>
    request<PlatformCompanyDetail>('/platform/companies/' + id + '/suspend', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  reactivateCompany: (id: string, body: ReactivateCompanyRequest) =>
    request<PlatformCompanyDetail>('/platform/companies/' + id + '/reactivate', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

/**
 * مركز ترحيل البيانات — نفس المسارات تحت الشركة (عضو له `data.import`) أو تحت لوحة
 * المنصة (المشغّل كخدمة للعميل)؛ الشاشة واحدة والفرق فى البادئة.
 */
export function migrationBase(companyId: string, asPlatform = false): string {
  return (asPlatform ? '/platform/companies/' : '/companies/') + companyId + '/migration';
}

export const migrationApi = {
  batches: (base: string) => request<MigrationBatchSummary[]>(base + '/batches'),

  createBatch: (base: string, body: CreateMigrationBatchRequest) =>
    request<MigrationBatchDto>(base + '/batches', { method: 'POST', body: JSON.stringify(body) }),

  batch: (base: string, batchId: string) =>
    request<MigrationBatchDto>(base + '/batches/' + batchId),

  deleteBatch: (base: string, batchId: string) =>
    request<void>(base + '/batches/' + batchId, { method: 'DELETE' }),

  /** الملف بايتات خام واسمه فى ترويسة — الخادم يقرأ الصيغة من المحتوى ولا يحفظه. */
  upload: (
    base: string,
    batchId: string,
    file: File,
    query: {
      kind: MigrationKind;
      sheet?: string;
      headerRow?: number;
      encoding?: 'utf-8' | 'windows-1256';
      profileId?: string;
    },
  ) =>
    request<MigrationFileDto>(
      base +
        '/batches/' +
        batchId +
        '/files?' +
        queryString({
          kind: query.kind,
          sheet: query.sheet,
          headerRow: query.headerRow ? String(query.headerRow) : undefined,
          encoding: query.encoding,
          profileId: query.profileId,
        }),
      {
        method: 'POST',
        headers: {
          'content-type': 'application/octet-stream',
          'x-file-name': encodeURIComponent(file.name),
        },
        body: file,
      },
    ),

  deleteFile: (base: string, batchId: string, fileId: string) =>
    request<void>(base + '/batches/' + batchId + '/files/' + fileId, { method: 'DELETE' }),

  updateMapping: (base: string, batchId: string, fileId: string, body: UpdateMappingRequest) =>
    request<MigrationFileDto>(base + '/batches/' + batchId + '/files/' + fileId + '/mapping', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  rows: (
    base: string,
    batchId: string,
    query: { kind: MigrationKind; filter?: string; q?: string; offset?: number; limit?: number },
  ) =>
    request<MigrationRowsResponse>(
      base +
        '/batches/' +
        batchId +
        '/rows?' +
        queryString({
          kind: query.kind,
          filter: query.filter,
          q: query.q,
          offset: query.offset !== undefined ? String(query.offset) : undefined,
          limit: query.limit !== undefined ? String(query.limit) : undefined,
        }),
    ),

  updateRows: (base: string, batchId: string, body: UpdateMigrationRowsRequest) =>
    request<{ updated: number }>(base + '/batches/' + batchId + '/rows', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteRows: (base: string, batchId: string, rowIds: string[]) =>
    request<{ deleted: number }>(base + '/batches/' + batchId + '/rows/delete', {
      method: 'POST',
      body: JSON.stringify({ rowIds }),
    }),

  mergeRows: (base: string, batchId: string, keepRowId: string, mergeRowIds: string[]) =>
    request<MigrationRowDto>(base + '/batches/' + batchId + '/rows/merge', {
      method: 'POST',
      body: JSON.stringify({ keepRowId, mergeRowIds }),
    }),

  reconciliation: (base: string, batchId: string) =>
    request<MigrationReconciliationResponse>(base + '/batches/' + batchId + '/reconciliation'),

  commit: (base: string, batchId: string, idempotencyKey: string) =>
    request<MigrationReportResponse & { replayed: boolean }>(
      base + '/batches/' + batchId + '/commit',
      { method: 'POST', headers: { 'idempotency-key': idempotencyKey } },
    ),

  reverse: (base: string, batchId: string, idempotencyKey: string, reason: string) =>
    request<MigrationBatchSummary & { replayed: boolean }>(
      base + '/batches/' + batchId + '/reverse',
      {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: JSON.stringify({ reason }),
      },
    ),

  report: (base: string, batchId: string) =>
    request<MigrationReportResponse>(base + '/batches/' + batchId + '/report'),

  profiles: (base: string, kind?: MigrationKind) =>
    request<MigrationProfileDto[]>(base + '/profiles?' + queryString({ kind })),

  /** القالب ملف xlsx يُنزَّل بكوكى الجلسة ثم يُحفظ من blob محلى. */
  downloadTemplate: async (base: string, kind: MigrationKind, fileName: string): Promise<void> => {
    const response = await fetch(BASE_URL + base + '/templates/' + kind, {
      credentials: 'include',
    });
    if (response.status === 401) throw new UnauthorizedError();
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  },
};

/** `problem+json` يحمل السبب فى `type`؛ أى جسم آخر لا يُفسَّر. */
async function problemOf(response: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await response.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

async function typeOf(response: Response): Promise<string | null> {
  const problem = await problemOf(response);
  return typeof problem?.type === 'string' ? problem.type : null;
}

async function detailOf(response: Response): Promise<string | undefined> {
  const problem = await problemOf(response);
  return typeof problem?.detail === 'string' ? problem.detail : undefined;
}
